/**
 * Client-agnostic recognition of a maintenance refusal.
 *
 * Grieg's read-only gate refuses a write during a maintenance window with a
 * *typed* 503 — a JSON body carrying `code: "MAINTENANCE"` — rather than a bare
 * 503 (NS-2442/NS-2443). That distinction is what lets the front end tell a
 * deliberate window apart from a genuine platform outage, so everything here
 * keys off the body rather than off the status code or the request URL.
 *
 * Nothing in this module is Axios-specific: the same parser serves the Axios
 * interceptor in {@link useMaintenanceMode} and the `fetch` wrapper below, so a
 * refusal is recognised whichever client issued the request (NS-2463).
 */

/** `code` carried by the typed maintenance 503 body from Grieg's read-only gate. */
export const MAINTENANCE_ERROR_CODE = 'MAINTENANCE';

/** Operator classification of a window, when Grieg supplies one. */
export type MaintenanceWindowType = 'scheduled' | 'emergency';

/** A recognised maintenance refusal, normalised across HTTP clients. */
export interface MaintenanceRefusal {
  /**
   * Whether reads continue to be served — i.e. whether this is a read-only
   * window (the normal case) or a full-service one. Defaults to true when the
   * body omits it, since the gate exists to keep reads available.
   */
  readsAvailable: boolean;
  /** Operator classification of the window, when supplied. */
  windowType?: MaintenanceWindowType | string;
  /** ISO-8601 expected end of the window, when known. */
  estimatedEnd?: string;
  /** Seconds until the caller may retry, derived from the `Retry-After` header. */
  retryAfterSeconds?: number;
  /** When the refusal was observed, so callers can disregard a stale one. */
  detectedAt: number;
}

/** The subset of a response this module needs, from any client. */
export interface MaintenanceResponseLike {
  status?: number;
  /** Parsed JSON body, or the raw string — both are handled. */
  body?: unknown;
  /** Raw `Retry-After` header value, if present. */
  retryAfter?: string | null;
}

interface MaintenanceBody {
  code?: unknown;
  readsAvailable?: unknown;
  windowType?: unknown;
  estimatedEnd?: unknown;
}

/**
 * Coerces a response body to an object. Axios normally parses JSON for us, but
 * hands back a raw string when the response lacks a JSON content type or a
 * transform is bypassed, and `fetch` bodies are always read as text here.
 */
const asObject = (body: unknown): MaintenanceBody | null => {
  if (typeof body === 'string') {
    try {
      const parsed: unknown = JSON.parse(body);
      return typeof parsed === 'object' && parsed !== null ? (parsed as MaintenanceBody) : null;
    } catch {
      return null;
    }
  }

  return typeof body === 'object' && body !== null ? (body as MaintenanceBody) : null;
};

/**
 * Reads `Retry-After`, which RFC 9110 allows to be either delta-seconds or an
 * HTTP-date. Both are reduced to seconds from now; anything else is ignored.
 */
export const parseRetryAfter = (value?: string | null): number | undefined => {
  if (!value) return undefined;

  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);

  const asDate = Date.parse(trimmed);
  if (!Number.isNaN(asDate)) return Math.max(0, Math.round((asDate - Date.now()) / 1000));

  return undefined;
};

/**
 * Case-insensitive header lookup that copes with both of Axios's header shapes
 * (a plain object on older responses, an `AxiosHeaders` instance on newer ones)
 * as well as a `fetch` `Headers` instance.
 */
export const getHeaderValue = (headers: unknown, name: string): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;

  const candidate = headers as Record<string, unknown> & { get?: (headerName: string) => unknown };

  if (typeof candidate.get === 'function') {
    const viaGetter = candidate.get(name);
    if (typeof viaGetter === 'string') return viaGetter;
  }

  const wanted = name.toLowerCase();
  const key = Object.keys(candidate).find((candidateKey) => candidateKey.toLowerCase() === wanted);

  return typeof key === 'string' && typeof candidate[key] === 'string' ? (candidate[key] as string) : undefined;
};

/**
 * Recognises a maintenance refusal from any client's response.
 *
 * Returns null for anything that is not a typed maintenance 503 — including a
 * bare 503, which is treated as an outage rather than a window. This is the
 * discrimination the whole feature rests on: callers must keep their existing
 * failure handling for a null result.
 */
export const parseMaintenanceRefusal = ({
  status,
  body,
  retryAfter,
}: MaintenanceResponseLike): MaintenanceRefusal | null => {
  if (status !== 503) return null;

  const parsed = asObject(body);
  if (parsed?.code !== MAINTENANCE_ERROR_CODE) return null;

  return {
    readsAvailable: parsed.readsAvailable !== false,
    windowType: typeof parsed.windowType === 'string' ? parsed.windowType : undefined,
    estimatedEnd: typeof parsed.estimatedEnd === 'string' ? parsed.estimatedEnd : undefined,
    retryAfterSeconds: parseRetryAfter(retryAfter),
    detectedAt: Date.now(),
  };
};

/**
 * Whether `url` targets a platform API, used only to scope the *outage*
 * takeover — a maintenance refusal is recognised from its body regardless of
 * where it came from.
 *
 * Relative URLs are resolved against the current origin and the match is made
 * on path, not on an origin string. The previous check,
 * `url.includes(`${window.location.origin}/api`)`, missed every relative call
 * (`/api/v1/...` — the form most of the app actually uses, including the flags
 * poll) and every call to an API served from another host (NS-2463).
 */
export const isApiRequestUrl = (url?: string): boolean => {
  if (!url) return false;

  try {
    const { pathname } = new URL(url, window.location.origin);
    return pathname.startsWith('/api/');
  } catch {
    return false;
  }
};

type RefusalListener = (refusal: MaintenanceRefusal | null) => void;

const listeners = new Set<RefusalListener>();
let lastRefusal: MaintenanceRefusal | null = null;

const notify = () => listeners.forEach((listener) => listener(lastRefusal));

/**
 * Records a recognised refusal so the UI can explain it. Write controls stay
 * enabled during a window by design, so this is how a user learns that the
 * specific action they just took was refused rather than merely broken.
 */
export const recordMaintenanceRefusal = (refusal: MaintenanceRefusal): void => {
  lastRefusal = refusal;
  notify();
};

/** Discards the recorded refusal, e.g. once the user has acknowledged it. */
export const clearMaintenanceRefusal = (): void => {
  lastRefusal = null;
  notify();
};

/** The most recent recognised refusal, or null if there has not been one. */
export const getLastMaintenanceRefusal = (): MaintenanceRefusal | null => lastRefusal;

/** Subscribes to refusal changes. Returns an unsubscribe function. */
export const subscribeToMaintenanceRefusal = (listener: RefusalListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Reads a 503 body without consuming the caller's copy. */
const readBodyText = async (response: Response): Promise<string | undefined> => {
  try {
    const source = typeof response.clone === 'function' ? response.clone() : response;
    return await source.text();
  } catch {
    return undefined;
  }
};

export interface MaintenanceAwareFetchResult {
  /** The response, untouched — its body has not been consumed. */
  response: Response;
  /** The refusal recognised on *this* response, or null. */
  refusal: MaintenanceRefusal | null;
}

/**
 * As {@link maintenanceAwareFetch}, but also returns the refusal recognised on
 * this specific response.
 *
 * Prefer this where the call site wants to explain the failure itself: checking
 * {@link getLastMaintenanceRefusal} instead would risk attributing an earlier
 * request's refusal to this one.
 */
export const fetchWithMaintenanceDetection = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<MaintenanceAwareFetchResult> => {
  const response = await fetch(input, init);

  if (response?.status !== 503) return { response, refusal: null };

  const refusal = parseMaintenanceRefusal({
    status: response.status,
    body: await readBodyText(response),
    retryAfter: getHeaderValue(response.headers, 'retry-after'),
  });

  if (refusal) recordMaintenanceRefusal(refusal);

  return { response, refusal };
};

/**
 * `fetch` that recognises a maintenance refusal, then hands the response back
 * untouched so existing call-site logic is unaffected.
 *
 * Use this in place of bare `fetch` anywhere a request can reach the platform.
 * The body is read from a clone, so the caller can still consume it.
 */
export const maintenanceAwareFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
  (await fetchWithMaintenanceDetection(input, init)).response;
